import {
  SUPPORT_SESSION_CODE_PARAM,
  SUPPORT_SESSION_PATH,
  type SupportSessionGrant,
  SupportSessionGrantSchema,
} from "@vidimo/shared";
import { useEffect, useState } from "react";
import { useTranslations } from "use-intl";
import { LoadingMessage } from "@/components/ui/loading-screen";
import { getApiUrl } from "@/lib/api-url";
import {
  holdSupportSession,
  isSupportSessionEndedPage,
} from "@/lib/support-session";

type Outcome = "ended" | "failed" | "invalid";

const HTTP_SERVER_ERROR = 500;

/**
 * Trades the code the CRM put in the fragment for the session. The code leaves
 * the address bar and the history entry before the request goes out.
 */
async function startFromLocation(): Promise<SupportSessionGrant | Outcome> {
  if (isSupportSessionEndedPage(window.location.search)) {
    return "ended";
  }
  const code = new URLSearchParams(window.location.hash.slice(1)).get(
    SUPPORT_SESSION_CODE_PARAM
  );
  window.history.replaceState(null, "", SUPPORT_SESSION_PATH);
  if (!code) {
    return "invalid";
  }
  try {
    const response = await fetch(getApiUrl("/support/session"), {
      body: JSON.stringify({ code }),
      credentials: "omit",
      headers: { "Content-Type": "application/json" },
      method: "POST",
    });
    // The code left the address bar above, so a refused one, malformed,
    // spent or rate-limited, can only be replaced from the CRM.
    if (!response.ok) {
      return response.status < HTTP_SERVER_ERROR ? "invalid" : "failed";
    }
    return SupportSessionGrantSchema.parse(await response.json());
  } catch {
    return "failed";
  }
}

let started: Promise<SupportSessionGrant | Outcome> | null = null;

/** Once per page load, so an effect that runs twice spends no code twice. */
function startOnce(): Promise<SupportSessionGrant | Outcome> {
  started ??= startFromLocation();
  return started;
}

/**
 * Where the CRM's "View as this user" lands, and where a support tab ends up
 * once its session is over (ADR-0143). Rendered instead of the app, since the
 * tab is neither signed in as anyone yet nor any longer.
 */
export function SupportSessionPage() {
  const t = useTranslations("shell.supportSession");
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  useEffect(() => {
    startOnce().then((result) => {
      if (typeof result === "string") {
        setOutcome(result);
        return;
      }
      holdSupportSession(result);
      window.location.replace("/");
    });
  }, []);

  useEffect(() => {
    // Only a tab the CRM opened can close itself; any other stays on the note.
    if (outcome === "ended") {
      window.close();
    }
  }, [outcome]);

  return (
    <main className="flex h-dvh items-center justify-center bg-background px-6 text-center text-foreground">
      {outcome === null ? (
        <LoadingMessage caption={t("opening")} />
      ) : (
        <div
          className="max-w-sm space-y-2"
          data-testid="support-session-outcome"
        >
          <h1 className="font-semibold text-lg">{t(`${outcome}.title`)}</h1>
          <p className="text-muted-foreground text-sm">
            {t(`${outcome}.description`)}
          </p>
        </div>
      )}
    </main>
  );
}
